import { setProgressBarCurrentStep } from '@avalara/bat-apps-shared/client/actions/progress-bar';
import { t } from '@avalara/bat-apps-shared/i18n';
import { SCol, SRow } from '@avalara/skylab-sdk/react';
import type { Dispatch } from '@avalara/types/actions';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { Pages } from '../../../../../types/offers';
import conf from '../../../conf';
import FormInput from '../../../containers/shared/forms/form-input';
import { isOfferFormOneIncomplete } from '../../../lib/shared-signup';
import { goToNextOfferStep } from '../../../lib/side-effects/offer-flow';
import { stepNames } from '../../../lib/side-effects/progress-bar';
import { selectState } from '../../../selectors';
import { isEmbeddedSelector } from '../../../selectors/offer';
import { setCurrentOfferPage } from '../../../store/offer';
import PasswordInput, { strongPasswordText } from '../../shared/password-input';
import SignInLink from '../sign-in-link';
import { FormContainer } from '../styled/forms';

type Props = {
  flow?: string;
};

const GetStarted = ({ flow }: Props) => {
  const {
    offer: { allowedUsers, initializeNewAccounts, offerFlow },
    sharedSignup,
  } = useSelector(selectState);

  const isEmbedded = useSelector(isEmbeddedSelector);
  const dispatch = useDispatch<Dispatch>();
  const history = useHistory();

  const disabled = isOfferFormOneIncomplete(sharedSignup, initializeNewAccounts);

  // biome-ignore lint/correctness/useExhaustiveDependencies: leaving as is
  useEffect(() => {
    dispatch(setCurrentOfferPage(Pages.GetStarted));
    const progressBarStep = initializeNewAccounts ? stepNames.createYourAccount : stepNames.nameAndEmail;
    dispatch(setProgressBarCurrentStep(progressBarStep));
  }, []);

  const handleClick = () => {
    if (offerFlow) dispatch(goToNextOfferStep({ history }));
    else if (flow) history.push({ pathname: `/${flow}/company`, search: window.location.search });
  };

  let headingText = 'Get started in three easy steps';
  if (initializeNewAccounts) headingText = 'Create your account';
  else if (offerFlow === 'ioss') headingText = 'Tell us about yourself';

  const isElrOrDbna = offerFlow === 'elr' || offerFlow === 'dbna';
  const elrDbnaSignInUrl =
    isElrOrDbna && offerFlow
      ? `${conf.CLIENT_URL}/auth/login?returnUrl=${encodeURIComponent(
          `${conf.BUY_CLIENT_URL}/${offerFlow}/activate${window.location.search}`,
        )}`
      : undefined;

  return (
    <FormContainer isEmbedded={isEmbedded}>
      <h1 data-ref='offer.get-started-header'>{headingText}</h1>

      {(allowedUsers !== 'NEW' || isElrOrDbna) && <SignInLink href={elrDbnaSignInUrl} dataRef='offer.login-button' />}

      <form aria-label={t('client.accessibility.form-for-name-and-email')}>
        <SRow>
          <SCol span='6' className='pad-right-sm'>
            <label htmlFor='firstName'>{t('client.labels.first-name')}</label>
            <FormInput fieldName='firstName' addPadding={true} />
          </SCol>
          <SCol span='6'>
            <label htmlFor='lastName'>{t('client.labels.last-name')}</label>
            <FormInput fieldName='lastName' addPadding={true} />
          </SCol>
        </SRow>
        <SRow>
          <SCol span='6' className='pad-right-sm'>
            <label htmlFor='email'>{t('client.labels.email-address')}</label>
            <FormInput fieldName='email' addPadding={true} type='email' validateDuringInput={true} />
          </SCol>
          <SCol span='6'>
            <label htmlFor='companyName'>{t('client.labels.company-name')}</label>
            <FormInput fieldName='companyName' addPadding={true} validateDuringInput={true} />
          </SCol>
        </SRow>
        {initializeNewAccounts && (
          <SRow>
            <SCol span='6' className='pad-right-sm margin-bottom-md'>
              <label htmlFor='password'>{t('client.labels.create-a-password')}</label>
              <p className='margin-bottom-sm'>{strongPasswordText}</p>
              <PasswordInput marginAboveError='12px' />
            </SCol>
          </SRow>
        )}
      </form>
      <hr className='pad-bottom-lg margin-top-xs' />
      <button
        type='button'
        className='primary icon-trailing'
        data-ref='offer.get-started-button'
        disabled={disabled}
        onClick={handleClick}
      >
        {t('client.buttons.next')}
      </button>
    </FormContainer>
  );
};

export default GetStarted;
